<template>
  <v-tooltip v-if="auth.check('create work orders')" location="top">
    <template v-slot:activator="{ props: tooltipProps }">
      <v-btn
        v-if="buttonType === 'text'"
        v-bind="tooltipProps"
        color="primary"
        depressed
        size="small"
        class="mr-1"
        @click="hasCoordinates && openDialog()"
      >
        {{ t('duplicate') }}
      </v-btn>
      <v-btn
        v-else
        v-bind="tooltipProps"
        @click.stop="hasCoordinates && openDialog()"
        icon
        size="small"
        color="white"
        class="mr-1 first show-icon"
      >
        <v-icon>mdi-content-copy</v-icon>
      </v-btn>
    </template>
    <span>
      {{ hasCoordinates ? t('duplicate') : t('duplicatingNotPossibleBecauseOfLocationWithoutCoordinates') }}
    </span>
  </v-tooltip>

  <v-dialog v-model="dialogVisible" max-width="800">
    <v-card :title="t('duplicate')">
      <template v-slot:text>
        <p class="mb-4">{{ t('selectDateAndCategoryForDuplicateWorkOrder') }}</p>

        <template
          v-for="(row, index) in dateRows"
          :key="index"
        >
          <v-row class="align-center" no-gutters>
            <v-col cols="4">
              <Datepicker
                :model-value="row.initialDate"
                @update:value-datetime="updateRowDate(index, $event)"
                :teleport="true"
              />
            </v-col>
            <v-col cols="3" class="pl-3">
              <Timepicker
                :model-value="row.initialTime"
                @update:value-datetime="updateRowTime(index, $event)"
                :teleport="true"
              />
            </v-col>
            <v-col cols="4" class="pl-3">
              <v-autocomplete
                :label="t('category')"
                :items="categoryItems"
                v-model="row.category"
                variant="outlined"
                hide-details
                class="category-dropdown"
              />
            </v-col>
            <v-col cols="1" class="d-flex justify-start pl-2">
              <v-icon
                v-if="dateRows.length > 1"
                size="small"
                class="remove-date"
                @click="removeDateRow(index)"
              >
                mdi-delete
              </v-icon>
            </v-col>
          </v-row>
          <div v-if="displayRowError(row)" class="text-red text-caption mb-2">
            {{ displayRowError(row) }}
          </div>
          <div v-else class="mb-2" />
        </template>
        <v-btn
          variant="text"
          density="comfortable"
          prepend-icon="mdi-plus-box"
          class="px-0 mt-2"
          @click="addDateRow"
        >
          {{ t('addDate') }}
        </v-btn>
      </template>

      <template v-slot:actions>
        <v-btn
          :text="t('save')"
          color="primary"
          :loading="submitting"
          :disabled="!hasAllValues"
          @click.stop="submit"
        />
        <v-btn
          :text="t('cancel')"
          :disabled="submitting"
          @click.stop="dialogVisible = false"
        />
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import Datepicker from '@/components/UI/Datepicker.vue'
import Timepicker from '@/components/UI/Timepicker.vue'
import Say from '@/utils/Say'
import sayErrorResponse from '@/mixins/sayErrorResponse'
import useProjectTabSelector from '@/composable/useProjectTabSelector'
import { dateAndTimeToDatetime, datetimeSplit } from '@/helpers'
import { useAuth } from '@/utils/auth'
import moment from 'moment'

const props = defineProps({
  workOrder: {
    type: Object,
    required: true
  },
  buttonType: {
    type: String as PropType<'text' | 'icon'>,
    required: true
  }
})

const emit = defineEmits(['duplicated'])

const store = useStore()
const router = useRouter()
const { t } = useI18n()
const { projectTabSelector } = useProjectTabSelector()
const auth = useAuth()

const dialogVisible = ref(false)
const submitting = ref(false)
const hasAttemptedSubmit = ref(false)

type TimeObject = { hours: number, minutes: number, seconds: number }
type DateRow = {
  initialDate: string | null
  initialTime: TimeObject | null
  date: string | null
  time: string | null
  category: string | null
}

const categoryItems = props.workOrder.is_for_equipment_movement
  ? [
    'Materiaal ophalen bij verhuur leverancier',
    'Materiaal terugbrengen naar verhuur leverancier',
    'Materiaal ophalen bij vestiging',
    'Materiaal brengen naar vestiging',
  ]
  : [
    'Plaatsen verkeersmaatregelen',
    'Retour verkeersmaatregelen',
    'Verkeer regelen',
    'Opdraaien verkeersmaatregelen',
    'Afdraaien verkeersmaatregelen',
    'Wegdekreiniging',
    'Afhalen',
    'Monteur(s) + trekkend voertuig',
    'Diverse',
  ]

const hasCoordinates = computed(() => {
  return !!(props.workOrder.location && props.workOrder.location.latitude && props.workOrder.location.longitude)
})

const jobId = computed(() => {
  return props.workOrder.job_id ?? props.workOrder.job?.id ?? null
})

const toTimeObject = (time: string | null): TimeObject | null => {
  if (!time) {
    return null
  }

  const [hours, minutes] = time.split(':')
  return { hours: Number(hours), minutes: Number(minutes), seconds: 0 }
}

const splitWorkOrderDatetime = (datetimeStart: any): { date: string | null, time: string | null } => {
  return (datetimeStart && typeof datetimeStart === 'object')
    ? { date: datetimeStart.date ?? null, time: datetimeStart.time ?? null }
    : datetimeSplit(datetimeStart)
}

const todayString = moment().format('YYYY-MM-DD')

const createRow = (overrides: { date?: string, time?: string | null, category?: string | null } = {}): DateRow => {
  const date = overrides.date ?? todayString
  const time = overrides.time ?? splitWorkOrderDatetime(props.workOrder.datetime_start).time
  const category = overrides.category ?? props.workOrder.category ?? null

  return {
    initialDate: date,
    initialTime: toTimeObject(time),
    date,
    time,
    category
  }
}

const dateRows = ref<DateRow[]>([createRow()])

const rowDatetime = (row: DateRow) => dateAndTimeToDatetime(row.date, row.time)

const getRowError = (row: DateRow): string | null => {
  if (!row.date) {
    return null
  }

  if (!row.category) {
    return t('categoryRequired')
  }

  return null
}

const displayRowError = (row: DateRow): string | null => {
  return hasAttemptedSubmit.value ? getRowError(row) : null
}

const hasAllValues = computed(() => {
  return dateRows.value.length > 0 && dateRows.value.every((row) => !!rowDatetime(row) && !!row.category)
})

const isValid = computed(() => {
  return hasAllValues.value && dateRows.value.every((row) => !getRowError(row))
})

const openDialog = () => {
  dateRows.value = [createRow()]
  hasAttemptedSubmit.value = false
  dialogVisible.value = true
}

const addDateRow = () => {
  const lastRow = dateRows.value[dateRows.value.length - 1]
  const nextDate = lastRow?.date
    ? moment(lastRow.date).add(1, 'day').format('YYYY-MM-DD')
    : todayString

  dateRows.value.push(createRow({ date: nextDate, time: lastRow?.time, category: lastRow?.category }))
}

const removeDateRow = (index: number) => {
  dateRows.value.splice(index, 1)
}

const updateRowDate = (index: number, value: string | null) => {
  dateRows.value[index].date = value
}

const updateRowTime = (index: number, value: string | null) => {
  dateRows.value[index].time = value
}

const submit = async () => {
  hasAttemptedSubmit.value = true

  if (!isValid.value) {
    return
  }

  submitting.value = true
  try {
    const workOrders = await store.dispatch('workOrders.duplicate', {
      id: props.workOrder.id,
      dates: dateRows.value.map((row) => ({ date: rowDatetime(row), category: row.category }))
    })

    Say('success', t('workOrderDuplicateSuccess'))
    dialogVisible.value = false
    emit('duplicated', workOrders)

    if (workOrders.length === 1) {
      router.push({ name: 'WorkOrdersView', params: { id: workOrders[0].id } })
    } else if (jobId.value) {
      projectTabSelector.value = 'Workorders'
      router.push({ name: 'JobsView', params: { id: jobId.value } })
    }
  } catch (err: any) {
    sayErrorResponse(err)
  } finally {
    submitting.value = false
  }
}
</script>

<style lang="scss" scoped>
.remove-date {
  cursor: pointer;

  &:hover {
    color: red;
  }
}
:deep(.category-dropdown .v-field),
:deep(.category-dropdown .v-field__input),
:deep(.category-dropdown .v-field__field) {
  height: 30px !important;
  max-height: 30px !important;
  min-height: 30px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
}
</style>
